import React, { createContext, useContext, useState, useCallback } from 'react';

type ToastType = 'success' | 'error' | 'info';
export type ToastItem = { id: number; type: ToastType; title?: string; message: string };

type ToastApi = {
  show: (t: Omit<ToastItem, 'id'>) => void;
  success: (message: string, title?: string) => void;
  error: (message: string, title?: string) => void;
  info: (message: string, title?: string) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

export const useToast = (): ToastApi => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast moet binnen <ToastProvider> gebruikt worden');
  return ctx;
};

let _id = 0;

const STYLE: Record<ToastType, { bar: string; icon: string; iconCls: string; titel: string }> = {
  success: { bar: 'bg-emerald-500', icon: '✓', iconCls: 'bg-emerald-100 text-emerald-700', titel: 'Gelukt' },
  error: { bar: 'bg-red-500', icon: '!', iconCls: 'bg-red-100 text-red-700', titel: 'Er ging iets mis' },
  info: { bar: 'bg-mfb-blue', icon: 'i', iconCls: 'bg-mfb-blue/10 text-mfb-blue', titel: 'Melding' },
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const remove = useCallback((id: number) => setToasts((ts) => ts.filter((t) => t.id !== id)), []);
  const show = useCallback((t: Omit<ToastItem, 'id'>) => {
    const id = ++_id;
    setToasts((ts) => [...ts, { ...t, id }]);
    setTimeout(() => setToasts((ts) => ts.filter((x) => x.id !== id)), t.type === 'error' ? 7000 : 5000);
  }, []);
  const api: ToastApi = {
    show,
    success: (message, title) => show({ type: 'success', message, title }),
    error: (message, title) => show({ type: 'error', message, title }),
    info: (message, title) => show({ type: 'info', message, title }),
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="fixed top-4 right-4 z-[100] flex w-[min(92vw,400px)] flex-col gap-3">
        {toasts.map((t) => (
          <ToastCard key={t.id} t={t} onClose={() => remove(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

const ToastCard: React.FC<{ t: ToastItem; onClose: () => void }> = ({ t, onClose }) => {
  const s = STYLE[t.type];
  return (
    <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg animate-[mfbToastIn_180ms_ease-out]">
      <span className={`absolute inset-y-0 left-0 w-1.5 ${s.bar}`} />
      <div className="flex items-start gap-3 py-3 pl-5 pr-3">
        <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-sm font-bold ${s.iconCls}`}>{s.icon}</span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-sm font-semibold text-mfb-blue">{t.title || s.titel}</p>
          <p className="mt-0.5 text-sm leading-snug text-slate-600 break-words">{t.message}</p>
        </div>
        <button onClick={onClose} aria-label="Sluiten" className="shrink-0 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">✕</button>
      </div>
      <style>{`@keyframes mfbToastIn{from{opacity:0;transform:translateX(16px)}to{opacity:1;transform:translateX(0)}}`}</style>
    </div>
  );
};

export default ToastProvider;
